import Dexie, { type Table } from 'dexie';
import { Temporal } from 'temporal-polyfill';
import type {
  ClassSeries,
  IsoDate,
  IsoInstant,
  Location,
  OccurrenceClaim,
  OccurrenceOverride,
} from '../domain/types';
import { makeOccurrenceKey, type OccurrenceKey } from '../domain/occurrenceKey';
import { nextClassColor } from '../domain/palette';
import { expandPastDates } from '../domain/recurrence';
import type { NewRecord, RecordPatch, Repository } from './repository';

/**
 * Dexie schema.
 *
 * Indexes exist only where a view actually queries:
 * - `locationId` for per-location filtering of series.
 * - `occurrenceKey` (unique) because overrides are addressed by it.
 * - `originalDate` so the calendar can fetch overrides for a visible window.
 * - `seriesId` for per-series lookups.
 *
 * `deletedAt` is deliberately NOT indexed: IndexedDB cannot index `null`, so live
 * rows would be missing from the index entirely and an index-based query would
 * silently return nothing. Tombstones are filtered in memory instead, which is
 * free at this scale (one instructor's schedule is hundreds of rows, not millions).
 *
 * Version 2 drops the `attendance` store rather than editing version 1 in place,
 * so an existing database is migrated cleanly instead of hitting a mismatch.
 *
 * Version 3 moves colour from the place to the class. Existing series are given a
 * colour by cycling the palette rather than inheriting their place's, because the
 * whole point of per-class colour is telling apart classes that share a place.
 *
 * Version 4 backfills `isSub: false` onto existing series. The field is not
 * indexed, but rows written before it existed would come back with it
 * `undefined`, and the type says `boolean` — the migration keeps stored data
 * honest instead of sprinkling `?? false` through the app.
 *
 * Version 5 adds shared classes: a `claims` store ("this occurrence of a shared
 * rotation is mine", addressed by occurrence key exactly like overrides) and a
 * `shared: false` backfill onto existing series, for the same honesty reason
 * as version 4.
 *
 * Version 6 makes REPEATING sub classes claim-based (one-time subs are
 * untouched). Existing rows predate that semantic, so their taught history is
 * claimed during the upgrade to keep it on the schedule and in stats.
 *
 * Version 7 makes shared and sub mutually exclusive. A row that somehow holds
 * both keeps the more specific description — it stays a sub — and drops the
 * shared flag. Claims are untouched: both flags meant claim-based, so the
 * class's occurrences do not change.
 */
class YogaDatabase extends Dexie {
  locations!: Table<Location, string>;
  series!: Table<ClassSeries, string>;
  overrides!: Table<OccurrenceOverride, string>;
  claims!: Table<OccurrenceClaim, string>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      locations: 'id, name',
      series: 'id, locationId, startDate',
      overrides: 'id, &occurrenceKey, seriesId, originalDate',
      attendance: 'id, &occurrenceKey, seriesId, recordedAt',
    });
    this.version(2).stores({
      attendance: null,
    });
    this.version(3).upgrade(async (transaction) => {
      const rows = await transaction.table<ClassSeries>('series').toArray();
      await Promise.all(
        rows.map((row, index) =>
          transaction
            .table<ClassSeries>('series')
            .update(row.id, { color: nextClassColor(index) } as Partial<ClassSeries>),
        ),
      );
    });
    this.version(4).upgrade(async (transaction) => {
      const rows = await transaction.table<ClassSeries>('series').toArray();
      await Promise.all(
        rows.map((row) =>
          transaction
            .table<ClassSeries>('series')
            .update(row.id, { isSub: row.isSub === true } as Partial<ClassSeries>),
        ),
      );
    });
    this.version(5)
      .stores({
        claims: 'id, &occurrenceKey, seriesId, originalDate',
      })
      .upgrade(async (transaction) => {
        const rows = await transaction.table<ClassSeries>('series').toArray();
        await Promise.all(
          rows.map((row) =>
            transaction
              .table<ClassSeries>('series')
              .update(row.id, { shared: row.shared === true } as Partial<ClassSeries>),
          ),
        );
      });
    this.version(6).upgrade(async (transaction) => {
      // Repeating sub classes became claim-based in v6: they only surface the
      // days the instructor picked. Rows from before that change have no
      // claims, so without help their history would vanish from stats and the
      // schedule. Claim their taught dates — the same thing the form does when
      // a class is switched to sub — and leave the future genuinely unpicked.
      const zoneById = new Map(
        (await transaction.table<Location>('locations').toArray()).map((row) => [
          row.id,
          row.timeZone,
        ]),
      );
      const claimsTable = transaction.table<OccurrenceClaim>('claims');
      const claimedKeys = new Set(
        (await claimsTable.toArray()).map((claim) => String(claim.occurrenceKey)),
      );
      const today = Temporal.Now.plainDateISO();
      const now = new Date().toISOString();

      const rows = await transaction.table<ClassSeries>('series').toArray();
      for (const row of rows) {
        if (row.deletedAt !== null || row.isSub !== true || row.shared === true) {
          continue;
        }
        if (row.recurrence.freq === 'once') {
          // One-time subs stay fully owned; nothing to claim.
          continue;
        }
        const timeZone = zoneById.get(row.locationId);
        if (timeZone === undefined) {
          continue; // Place removed: the schedule already hides this series.
        }
        for (const date of expandPastDates(row, timeZone, today)) {
          const originalDate = date.toString();
          const occurrenceKey = makeOccurrenceKey(row.id, originalDate);
          if (claimedKeys.has(String(occurrenceKey))) {
            continue;
          }
          await claimsTable.add({
            id: `claim-migrated-${row.id}-${originalDate}`,
            createdAt: now,
            updatedAt: now,
            deletedAt: null,
            seriesId: row.id,
            occurrenceKey,
            originalDate,
          });
        }
      }
    });
    this.version(7).upgrade(async (transaction) => {
      const rows = await transaction.table<ClassSeries>('series').toArray();
      await Promise.all(
        rows
          .filter((row) => row.shared === true && row.isSub === true)
          .map((row) =>
            transaction
              .table<ClassSeries>('series')
              .update(row.id, { shared: false } as Partial<ClassSeries>),
          ),
      );
    });
  }
}

export interface Clock {
  now(): IsoInstant;
}

export interface IdSource {
  next(): string;
}

const systemClock: Clock = { now: () => new Date().toISOString() };

const uuidSource: IdSource = {
  next: () =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : // Deterministic-enough fallback for environments without WebCrypto.
        `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
};

export interface DexieRepositoryOptions {
  readonly databaseName?: string;
  readonly clock?: Clock;
  readonly ids?: IdSource;
}

export class DexieRepository implements Repository {
  private readonly db: YogaDatabase;
  private readonly clock: Clock;
  private readonly ids: IdSource;

  constructor(options: DexieRepositoryOptions = {}) {
    this.db = new YogaDatabase(options.databaseName ?? 'yoga-scheduler');
    this.clock = options.clock ?? systemClock;
    this.ids = options.ids ?? uuidSource;
  }

  /** Exposed for tests so each case can start from a clean database. */
  async clear(): Promise<void> {
    await this.db.transaction(
      'rw',
      [this.db.locations, this.db.series, this.db.overrides, this.db.claims],
      async () => {
        await Promise.all([
          this.db.locations.clear(),
          this.db.series.clear(),
          this.db.overrides.clear(),
          this.db.claims.clear(),
        ]);
      },
    );
  }

  close(): void {
    this.db.close();
  }

  private stamp<T>(input: NewRecord<T>): T {
    const now = this.clock.now();
    return {
      ...input,
      id: this.ids.next(),
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    } as T;
  }

  // ---------------------------------------------------------------- locations

  async listLocations(): Promise<Location[]> {
    const rows = await this.db.locations.filter(isLive).toArray();
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }

  async getLocation(id: string): Promise<Location | undefined> {
    return live(await this.db.locations.get(id));
  }

  async createLocation(input: NewRecord<Location>): Promise<Location> {
    const record = this.stamp<Location>(input);
    await this.db.locations.add(record);
    return record;
  }

  async updateLocation(id: string, patch: RecordPatch<Location>): Promise<Location> {
    return this.patch(this.db.locations, id, patch, 'location');
  }

  async deleteLocation(id: string): Promise<void> {
    await this.tombstone(this.db.locations, id);
  }

  // ------------------------------------------------------------------- series

  async listSeries(): Promise<ClassSeries[]> {
    const rows = await this.db.series.filter(isLive).toArray();
    return rows.sort(
      (a, b) => a.startTime.localeCompare(b.startTime) || a.title.localeCompare(b.title),
    );
  }

  async getSeries(id: string): Promise<ClassSeries | undefined> {
    return live(await this.db.series.get(id));
  }

  async createSeries(input: NewRecord<ClassSeries>): Promise<ClassSeries> {
    const record = this.stamp<ClassSeries>(input);
    await this.db.series.add(record);
    return record;
  }

  async updateSeries(id: string, patch: RecordPatch<ClassSeries>): Promise<ClassSeries> {
    return this.patch(this.db.series, id, patch, 'series');
  }

  async deleteSeries(id: string): Promise<void> {
    await this.tombstone(this.db.series, id);
  }

  // ---------------------------------------------------------------- overrides

  async listOverrides(): Promise<OccurrenceOverride[]> {
    return this.db.overrides.filter(isLive).toArray();
  }

  async listOverridesInRange(from: IsoDate, to: IsoDate): Promise<OccurrenceOverride[]> {
    // ISO dates sort lexicographically, so a string range query is exact here.
    const rows = await this.db.overrides
      .where('originalDate')
      .between(from, to, true, true)
      .toArray();
    return rows.filter((row) => row.deletedAt === null);
  }

  async getOverride(occurrenceKey: OccurrenceKey): Promise<OccurrenceOverride | undefined> {
    return live(await this.db.overrides.where('occurrenceKey').equals(occurrenceKey).first());
  }

  async putOverride(input: NewRecord<OccurrenceOverride>): Promise<OccurrenceOverride> {
    return this.upsertByKey(this.db.overrides, input, input.occurrenceKey);
  }

  async clearOverride(occurrenceKey: OccurrenceKey): Promise<void> {
    const existing = await this.db.overrides.where('occurrenceKey').equals(occurrenceKey).first();
    if (existing) {
      await this.tombstone(this.db.overrides, existing.id);
    }
  }

  // ------------------------------------------------------------------- claims

  async listClaims(): Promise<OccurrenceClaim[]> {
    return this.db.claims.filter(isLive).toArray();
  }

  async getClaim(occurrenceKey: OccurrenceKey): Promise<OccurrenceClaim | undefined> {
    return live(await this.db.claims.where('occurrenceKey').equals(occurrenceKey).first());
  }

  async putClaim(input: NewRecord<OccurrenceClaim>): Promise<OccurrenceClaim> {
    return this.upsertByKey(this.db.claims, input, input.occurrenceKey);
  }

  async clearClaim(occurrenceKey: OccurrenceKey): Promise<void> {
    const existing = await this.db.claims.where('occurrenceKey').equals(occurrenceKey).first();
    if (existing) {
      await this.tombstone(this.db.claims, existing.id);
    }
  }

  // ------------------------------------------------------------------ helpers

  private async patch<
    T extends { id: string; updatedAt: IsoInstant; deletedAt: IsoInstant | null },
  >(table: Table<T, string>, id: string, changes: RecordPatch<T>, label: string): Promise<T> {
    const existing = await table.get(id);
    if (!existing || existing.deletedAt !== null) {
      throw new Error(`Cannot update ${label} "${id}": not found`);
    }
    const updated = { ...existing, ...changes, updatedAt: this.clock.now() } as T;
    await table.put(updated);
    return updated;
  }

  private async tombstone<
    T extends { id: string; updatedAt: IsoInstant; deletedAt: IsoInstant | null },
  >(table: Table<T, string>, id: string): Promise<void> {
    const existing = await table.get(id);
    if (!existing || existing.deletedAt !== null) {
      return;
    }
    const now = this.clock.now();
    await table.put({ ...existing, deletedAt: now, updatedAt: now } as T);
  }

  /**
   * Overrides are one-per-occurrence, so a write replaces any existing row for
   * that key — including reviving a tombstoned one, which keeps the id stable
   * across a cancel/uncancel/cancel cycle.
   */
  private async upsertByKey<
    T extends {
      id: string;
      occurrenceKey: OccurrenceKey;
      createdAt: IsoInstant;
      updatedAt: IsoInstant;
      deletedAt: IsoInstant | null;
    },
  >(table: Table<T, string>, input: NewRecord<T>, occurrenceKey: OccurrenceKey): Promise<T> {
    const existing = await table.where('occurrenceKey').equals(occurrenceKey).first();
    const now = this.clock.now();
    if (existing) {
      const updated = { ...existing, ...input, updatedAt: now, deletedAt: null } as T;
      await table.put(updated);
      return updated;
    }
    const record = this.stamp<T>(input);
    await table.add(record);
    return record;
  }
}

function isLive(row: { deletedAt: IsoInstant | null }): boolean {
  return row.deletedAt === null;
}

function live<T extends { deletedAt: IsoInstant | null }>(row: T | undefined): T | undefined {
  return row && row.deletedAt === null ? row : undefined;
}
